/**
 * Single source of truth for phone handling on every website lead form.
 *
 * History: five forms each carried their own country-code list and their own
 * copy of the digit-strip / prefix logic. The lists drifted (ViewingForm and
 * the callback section still only offered +27/+1/+44/+61 after the others
 * gained the SADC codes on 2026-09-09/11), so Namibian visitors on those two
 * forms were still normalised to nonexistent +27 numbers (Meta 131026).
 *
 * DEFAULT STAYS SOUTH AFRICA. The form submits the full international digits;
 * the Cloud Functions' normalizePhoneE164 passes those through unchanged.
 */

export interface CountryCodeOption {
  code: string;
  label: string;
}

export const DEFAULT_COUNTRY_CODE = '27';

export const COUNTRY_CODES: readonly CountryCodeOption[] = [
  { code: '27', label: '🇿🇦 +27' },
  { code: '264', label: '🇳🇦 +264' },
  { code: '267', label: '🇧🇼 +267' },
  { code: '268', label: '🇸🇿 +268' },
  { code: '263', label: '🇿🇼 +263' },
  { code: '258', label: '🇲🇿 +258' },
  { code: '44', label: '🇬🇧 +44' },
  { code: '1', label: '🇺🇸 +1' },
  { code: '61', label: '🇦🇺 +61' },
];

/** Minimum digits (national part) we accept before submitting a lead. */
export const MIN_PHONE_DIGITS = 9;

/** Digits only, e.g. "082 123-4567" → "0821234567". */
export function phoneDigits(raw: string): string {
  return (raw || '').replace(/\D/g, '');
}

/** True when the visitor typed enough digits to be a real number. */
export function isPlausiblePhone(raw: string): boolean {
  return phoneDigits(raw).length >= MIN_PHONE_DIGITS;
}

/**
 * Full international digits (no "+"): strip spacing and any leading 0, then
 * prefix the selected country code unless the visitor already typed it.
 * "082 123 4567" + "27" → "27821234567"; "27821234567" + "27" → unchanged.
 */
export function toInternationalDigits(raw: string, countryCode: string): string {
  const digits = phoneDigits(raw);
  const cc = phoneDigits(countryCode) || DEFAULT_COUNTRY_CODE;
  return digits.startsWith(cc) ? digits : `${cc}${digits.replace(/^0+/, '')}`;
}
